<template>
    <section
        class="bonusomat-board"
        :class="{
            'bonusomat-board--disabled': hasActiveQuest,
        }"
    >
        <TransitionExpand>
            <div class="bonusomat-board__notification" v-if="showActiveQuestBanner && !isBoardCompleted">
                <div
                    class="bonusomat-slots__action-bar bonusomat-slots__action-bar--inactive bonusomat-slots__action-bar--separated"
                >
                    <WebpImage
                        src="/frontend/web/img/casino/bonusomat/automat/automat.png?v=1"
                        alt="Bonusomat"
                        class="bonusomat-slots__bar-image"
                    />
                    <DsIcon name="b-mat" class="bonusomat-slots__bar-icon" />
                    <div class="bonusomat-slots__group">
                        <div class="bonusomat-slots__text">
                            Během hraní Questu
                            <span class="bonusomat-slots__text--thin">se neplní úkoly Bonusomatu</span>
                        </div>
                        <DsButton size="sm" type="error" icon-right="cross" @click="deactivateQuest('main')">
                            Ukončit Quest
                        </DsButton>
                    </div>
                </div>
            </div>
        </TransitionExpand>
        <template v-if="isBoardDataLoaded && isPlayerDataLoaded && isDefaultDrawDataLoaded">
            <BonusomatBoardCompleted v-if="isBoardCompleted" />
            <template v-else>
                <TypographyHeadline type="section" theme="dark" class="bonusomat-board__filter">
                    Úkoly
                    <BoardFilter v-model="filterValue" :options="filterOptions" />
                </TypographyHeadline>
                <TransitionGroup
                    name="board-task"
                    tag="div"
                    class="bonusomat-board__board"
                    v-if="boardData?.tasks.length"
                >
                    <BoardTask
                        v-for="task in tasksFiltered"
                        :progress-load-counter="task.position + '-' + progressLoadCounter.toString()"
                        :key="task.position"
                        :name="task.name"
                        :icon="task.icon"
                        :repeatable="task.repeatable"
                        :completed="task.completed"
                        :progress="task.progress"
                        @click="openModal(task)"
                        :is-active="task.position === activeTask?.position"
                    />
                    <span v-if="tasksFiltered.length === 0 && filterValue === BonusomatFilterValues.Completed">
                        Zatím zde nemáte žádné splněné úkoly, tak neváhejte a pusťte se do akce.
                    </span>
                </TransitionGroup>
                <div class="bonusomat-placeholder" v-else>
                    <div class="bonusomat-placeholder__text">Při načítání úkolů nastala chyba</div>
                    <div class="bonusomat-placeholder__subtext">Zkuste to prosím později</div>
                </div>
                <BoardRules :min-bet-amount="boardData?.minimumBetAmount" :rules-lik="boardData?.rules" />
            </template>
        </template>
        <template v-else>
            <TypographyHeadline type="section" theme="dark" class="bonusomat-board__filter">Úkoly</TypographyHeadline>
            <div class="bonusomat-board__board">
                <BoardTask
                    v-for="index in 45"
                    :key="index"
                    class="bonusomat-placeholder__skeleton"
                    :style="{
                        animationDelay: `${index * 0.01}s`,
                    }"
                />
            </div>
        </template>
        <DsModalDialog
            v-model="isModalOpen"
            @update:model-value="closedModal"
            css-class="bonusomat-board__modal bonusomat-modal"
            theme="dark"
            :top-divider-visible="activeTaskGames?.length > 0"
            :bottom-divider-visible="false"
            content-scrollable
        >
            <template #top>
                <DsTopSlotHeadlineSubheadline :headline="activeTask?.name" />
                <p class="bonusomat-modal__description" v-html="activeTask?.description"></p>
            </template>
            <template #content>
                <div class="bonusomat-board__modal__games" v-if="activeTaskGames?.length">
                    <div
                        class="bonusomat-board__modal__game"
                        v-for="game in activeTaskGames"
                        :key="game.id"
                        @click="openGameClick(game)"
                    >
                        <WebpImage
                            v-if="game.icon"
                            :src="game.icon"
                            :alt="`Ikona hry ${game.name}`"
                            css-class="bonusomat-board__modal__game__img"
                        />
                        <div
                            v-else
                            class="bonusomat-board__modal__game__img bonusomat-board__modal__game__img--no-icon"
                        ></div>
                        {{ game.name }}
                    </div>
                </div>
            </template>
            <template #bottom>
                <div class="bonusomat-modal__footer">
                    <div class="bonusomat-modal__footer__progress-wrapper">
                        <BonusomatProgress
                            client-id="board-task-detail"
                            :progress="activeTask?.progress"
                            show-percentage-number
                        />
                        <div class="bonusomat-modal__footer__texts">
                            <div class="bonusomat-modal__footer__state">{{ modalFooterText }}</div>
                        </div>
                    </div>
                </div>
            </template>
        </DsModalDialog>
    </section>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import DsModalDialog from "@sazka/design-system/src/modal-dialog/ds-modal-dialog.vue";
import DsTopSlotHeadlineSubheadline from "@sazka/design-system/src/top-slot/ds-top-subheadline.vue";
import TransitionExpand from "@sazka/design-system/src/transitions/transition-expand.vue";
import TypographyHeadline from "@sazka/design-system/src/typography/typography-headline.vue";
import { useLoyaltyPlayerStatus } from "@/loyalty/composables/use-loyalty-player-status";
import BonusomatBoardCompleted from "./bonusomat-board-completed.vue";
import BoardFilter from "./bonusomat-board-filter.vue";
import BoardRules from "./bonusomat-board-rules.vue";
import BoardTask from "./bonusomat-board-task.vue";
import useBonusomatData from "../../composables/use-bonusomat-data";
import { BonusomatBoardTask } from "../../models/bonusomat-board";
import { BoardFilterOption, BonusomatFilterValues } from "../../models/bonusomat-board-filter";
import { BonusomatQuestGame } from "../../models/bonusomat-quest-detail";
import BonusomatProgress from "../shared/bonusomat-progress.vue";

const { bonusomatPlayerStatus } = useLoyaltyPlayerStatus();
const { isPlayerDataLoaded, remainingSpinsCount } = bonusomatPlayerStatus;

const {
    isBoardDataLoaded,
    isDefaultDrawDataLoaded,
    hasActiveQuest,
    boardData,
    gamesData,
    deactivateQuest,
    openGame,
    loadGamesData,
} = useBonusomatData();

const showActiveQuestBanner = computed(() => {
    return remainingSpinsCount.value > 0 && hasActiveQuest.value;
});

const isBoardCompleted = computed(() => boardData.value?.completed);

const filterValue = ref<BonusomatFilterValues>(BonusomatFilterValues.All);
const filterOptions: BoardFilterOption[] = [
    {
        value: BonusomatFilterValues.All,
        text: "Vše",
    },
    {
        value: BonusomatFilterValues.Uncompleted,
        text: "Nesplněné",
    },
    {
        value: BonusomatFilterValues.Completed,
        text: "Splněné",
    },
    {
        value: BonusomatFilterValues.Repeating,
        text: "Opakující se",
    },
];

const tasksFiltered = computed<BonusomatBoardTask[]>(() => {
    return boardData.value?.tasks.filter(({ completed, repeatable }) => {
        if (filterValue.value === BonusomatFilterValues.Completed) return completed;
        if (filterValue.value === BonusomatFilterValues.Repeating) return repeatable;
        if (filterValue.value === BonusomatFilterValues.Uncompleted) return !completed;
        if (filterValue.value === BonusomatFilterValues.All) return true;
    });
});

// used to reload progress components on certain actions (fixes issue on IOS where progress animation doesn't work reliably by itself)
const progressLoadCounter = ref(0);

const isModalOpen = ref(false);
const activeTask = ref<BonusomatBoardTask>(null);
const activeTaskGames = computed(() => gamesData.value.filter((game) => activeTask.value?.games?.includes(game.id)));

const modalFooterText = computed(() => {
    const taskProgress = activeTask.value?.progress;
    return taskProgress === 100
        ? "Dokončeno"
        : `Zbývá ${100 - (taskProgress > 99 ? Math.floor(taskProgress) : Math.round(taskProgress))} %`;
});

const openModal = (task: BonusomatBoardTask) => {
    isModalOpen.value = true;

    if (!gamesData.value?.length) {
        loadGamesData();
    }

    activeTask.value = task;
};

watch(filterValue, async () => {
    setTimeout(() => {
        progressLoadCounter.value++;
    }, 500);
});

const closedModal = () => {
    setTimeout(() => {
        progressLoadCounter.value++;
    }, 200);

    activeTask.value = null;
};

const openGameClick = async (game: BonusomatQuestGame) => {
    openGame(game.id, game.type);

    activeTask.value = null;
    isModalOpen.value = false;
};
</script>
